import * as React from "react";
import { Activity, ArrowRight, Cookie, Smartphone, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const collectionData = [{
  id: "personal",
  iconName: "user",
  title: "Portfolio Intelligence",
  description: "We curate your investment history, asset allocations, and specific risk parameters. This data is leveraged exclusively to craft bespoke financial strategies and precise performance reporting."
}, {
  id: "usage",
  iconName: "activity",
  title: "Market Momentum",
  description: "Our systems monitor global market shifts and economic indicators in real-time. This intelligence allows us to identify emerging opportunities and manage institutional-grade volatility effectively."
}, {
  id: "cookies",
  iconName: "cookie",
  title: "Secure Verification",
  description: "We maintain stringent identity protocols to ensure the highest level of security. This includes verifying institutional credentials in compliance with rigorous global financial regulations."
}, {
  id: "device",
  iconName: "smartphone",
  title: "Interface Optimization",
  description: "We analyze technical interaction data to refine our digital wealth platforms, ensuring a seamless, high-performance experience across all your secure mobile and desktop devices."
}];
const iconMap: Record<string, React.ElementType> = {
  user: User,
  activity: Activity,
  cookie: Cookie,
  smartphone: Smartphone
};
export default function DataCollectionUsage() {
  return <section data-section-id="165216" id="data-we-collect" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm font-default font-medium text-primary mb-6">
            Asset Intelligence
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-6">
            Data Collection & Usage
          </h2>
          <p className="text-lg md:text-xl font-default font-light text-muted-foreground leading-relaxed">
            We operate with absolute transparency regarding your financial data. 
            Discover how we utilize proprietary intelligence to provide a secure, 
            optimized, and high-performance investment experience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {collectionData.map((item, i) => {
          const IconComponent = iconMap[item.iconName];
          return <Card key={item.id} data-index={i} className="border-border/40 bg-card/40 backdrop-blur-md transition-all duration-500 hover:bg-card hover:shadow-xl hover:border-primary/30 group rounded-xl">
                <CardHeader className="flex flex-row items-start gap-5 pb-4">
                  <div className="bg-primary/10 p-3 rounded-xl group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                    <IconComponent className="w-6 h-6 text-primary group-hover:text-primary-foreground" />
                  </div>
                  <div className="space-y-1">
                    <CardTitle className="text-xl font-default font-semibold tracking-tight text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pl-[4.5rem]">
                  <p className="text-muted-foreground font-default font-light leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="rounded-full px-8 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
            <Link to="#">
              View Governance Framework
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <p className="text-sm font-default font-medium text-muted-foreground text-center sm:text-left">
            Your assets are protected by institutional-grade encryption.
          </p>
        </div>
      </div>
    </section>;
}